import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { makeRedirectUri } from 'expo-auth-session';
import * as WebBrowser from 'expo-web-browser';
import { signInWithPopup, signInWithRedirect, getRedirectResult, signOut as firebaseSignOut, signInWithCredential, GoogleAuthProvider, TwitterAuthProvider, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, googleProvider, twitterProvider } from '../config/firebase';
import { getFirestore, doc, setDoc } from 'firebase/firestore'; // Import Firestore

// استيراد وإعداد Google SDK
let GoogleSignin: any = null;
let isNativeEnvironment = false;

// التحقق من البيئة
try {
  // فحص إذا كنا في بيئة React Native حقيقية
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    isNativeEnvironment = true;
    const { GoogleSignin: GoogleSigninImport } = require('@react-native-google-signin/google-signin');
    GoogleSignin = GoogleSigninImport;

    GoogleSignin.configure({
      webClientId: '39512678906-akhncdu13elitnjep2pulk0mm4vh3m6a.apps.googleusercontent.com',
      offlineAccess: true,
      forceCodeForRefreshToken: true,
      requestIdToken: true,
    });
    console.log('Google SDK configured for native platform');
  }
} catch (error) {
  console.log('Google SDK not available, will use Firebase Auth for web:', error.message);
}

WebBrowser.maybeCompleteAuthSession();

interface User {
  id: string;
  name: string;
  email: string;
  photoUrl?: string;
  provider?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithTwitter: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Firestore instance
const db = getFirestore();

// Function to save user data to Firestore
const saveUserToFirestore = async (user: User) => {
  try {
    console.log('🗄️ [FIRESTORE] بدء حفظ المستخدم في Firestore...', user);
    
    // التحقق من وجود ID على الأقل
    if (!user.id) {
      console.error('❌ [FIRESTORE] Invalid user data: missing id', user);
      return;
    }
    console.log('✅ [FIRESTORE] معرف المستخدم موجود:', user.id);

    // التحقق من حالة Firestore
    console.log('🗄️ [FIRESTORE] التحقق من حالة Firestore...', {
      dbInstance: !!db,
      firestoreType: typeof db
    });

    // تنظيف البيانات - البريد الإلكتروني اختياري للمزودين الاجتماعيين
    const userData = {
      id: user.id,
      name: user.name || 'مستخدم',
      // جعل البريد الإلكتروني اختياري وفقط إذا كان موجود وليس فارغ
      ...(user.email && user.email.trim() !== '' && { email: user.email }),
      ...(user.photoUrl && { photoUrl: user.photoUrl }),
      provider: user.provider || 'unknown',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    console.log('📝 [FIRESTORE] تم تحضير بيانات المستخدم للحفظ:', userData);

    console.log('🗄️ [FIRESTORE] إنشاء مرجع المستخدم...');
    const userRef = doc(db, 'users', user.id);
    console.log('🗄️ [FIRESTORE] تم إنشاء مرجع المستخدم:', userRef.path);
    
    console.log('🗄️ [FIRESTORE] بدء عملية setDoc...');
    await setDoc(userRef, userData, { merge: true });
    console.log('✅ [FIRESTORE] تم حفظ المستخدم في Firestore بنجاح!');
  } catch (error) {
    console.error('💥 [FIRESTORE] خطأ في حفظ المستخدم في Firestore:', error);
    console.error('💥 [FIRESTORE] تفاصيل الخطأ:', {
      errorMessage: error.message,
      errorCode: error.code,
      errorStack: error.stack
    });
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      await loadStoredAuth();
      await checkRedirectResult();
    };
    initAuth();
  }, []);

  // التحقق من نتائج Redirect عند تحميل التطبيق
  const checkRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth);
      if (result?.user && !user) {
        console.log('وجدت نتيجة redirect، معالجة المستخدم...');
        const firebaseUser = result.user;

        let provider = 'unknown';
        if (firebaseUser.providerData.length > 0) {
          const providerId = firebaseUser.providerData[0].providerId;
          if (providerId === 'twitter.com') provider = 'twitter';
          else if (providerId === 'google.com') provider = 'google';
        }

        const userData: User = {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || 'مستخدم',
          email: firebaseUser.email || '',
          photoUrl: firebaseUser.photoURL || undefined,
          provider: provider,
        };

        await saveUserToFirestore(userData);
        setUser(userData);
        await AsyncStorage.setItem('user', JSON.stringify(userData));
        console.log('تم معالجة المستخدم من redirect بنجاح');
      }
    } catch (error) {
      console.error('خطأ في التحقق من redirect result:', error);
    }
  };

  const loadStoredAuth = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('خطأ في تحميل بيانات المصادقة:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);

      // استخدام Firebase Auth لتسجيل الدخول
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;

      const userData: User = {
        id: user.uid,
        name: user.displayName || 'المستخدم',
        email: user.email || '',
        photoUrl: user.photoURL || undefined,
        provider: 'email',
      };

      // حفظ في Firestore
      await saveUserToFirestore(userData);

      setUser(userData);
      await AsyncStorage.setItem('user', JSON.stringify(userData));
    } catch (error: any) {
      console.error('خطأ في تسجيل الدخول:', error);

      // ترجمة أخطاء Firebase للعربية
      let errorMessage = 'فشل في تسجيل الدخول';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'لا يوجد حساب بهذا البريد الإلكتروني';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'كلمة المرور غير صحيحة';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'عنوان البريد الإلكتروني غير صحيح';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'تم تجاوز عدد المحاولات المسموحة، حاول لاحقاً';
      }

      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      console.log('🚀 [SIGNUP] بدء عملية إنشاء الحساب...', { 
        email, 
        name, 
        passwordLength: password.length,
        emailLength: email.length,
        nameLength: name.length 
      });
      
      setIsLoading(true);
      console.log('🔄 [SIGNUP] تم تعيين isLoading إلى true');

      // التحقق من صحة البيانات
      console.log('✅ [SIGNUP] التحقق من صحة البيانات...');
      if (!email || !password || !name) {
        console.error('❌ [SIGNUP] بيانات ناقصة:', { 
          email: !!email, 
          password: !!password, 
          name: !!name,
          emailValue: email,
          passwordValue: password ? '[HIDDEN]' : '',
          nameValue: name
        });
        throw new Error('جميع الحقول مطلوبة');
      }
      console.log('✅ [SIGNUP] جميع البيانات موجودة');

      // التحقق من حالة Firebase Auth
      console.log('🔥 [SIGNUP] التحقق من حالة Firebase Auth...', {
        authInstance: !!auth,
        authCurrentUser: auth?.currentUser?.uid || 'لا يوجد مستخدم حالي'
      });

      // إنشاء حساب جديد في Firebase
      console.log('🔥 [SIGNUP] بدء إنشاء المستخدم في Firebase...');
      console.log('🔥 [SIGNUP] استدعاء createUserWithEmailAndPassword...');
      
      const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
      
      console.log('🔥 [SIGNUP] تم إنشاء المستخدم في Firebase بنجاح!', {
        uid: result.user.uid,
        email: result.user.email,
        emailVerified: result.user.emailVerified,
        displayName: result.user.displayName
      });
      
      const firebaseUser = result.user;

      // تحديث الملف الشخصي للمستخدم بالاسم
      console.log('👤 [SIGNUP] بدء تحديث الملف الشخصي...');
      console.log('👤 [SIGNUP] استدعاء updateProfile مع الاسم:', name.trim());
      
      await updateProfile(firebaseUser, {
        displayName: name.trim()
      });
      
      console.log('👤 [SIGNUP] تم تحديث الملف الشخصي بنجاح');
      console.log('👤 [SIGNUP] displayName الجديد:', firebaseUser.displayName);

      // إنشاء بيانات المستخدم
      console.log('📝 [SIGNUP] إنشاء بيانات المستخدم...');
      const userData: User = {
        id: firebaseUser.uid,
        name: name.trim(),
        email: email.trim(),
        photoUrl: firebaseUser.photoURL || undefined,
        provider: 'email',
      };
      console.log('📝 [SIGNUP] تم إنشاء بيانات المستخدم:', userData);

      // حفظ في Firestore أولاً
      console.log('💾 [SIGNUP] بدء حفظ البيانات في Firestore...');
      console.log('💾 [SIGNUP] استدعاء saveUserToFirestore...');
      
      await saveUserToFirestore(userData);
      
      console.log('💾 [SIGNUP] تم حفظ البيانات في Firestore بنجاح');

      // ثم حفظ محلياً
      console.log('📱 [SIGNUP] بدء حفظ البيانات محلياً...');
      console.log('📱 [SIGNUP] استدعاء setUser...');
      
      setUser(userData);
      
      console.log('📱 [SIGNUP] استدعاء AsyncStorage.setItem...');
      await AsyncStorage.setItem('user', JSON.stringify(userData));
      
      console.log('🎉 [SIGNUP] تم إنشاء الحساب وحفظ البيانات بنجاح!', {
        finalUserData: userData,
        authCurrentUser: auth?.currentUser?.uid || 'لا يوجد مستخدم حالي'
      });
      
      console.log('🔄 [SIGNUP] تعيين isLoading إلى false...');
      setIsLoading(false);
      
      console.log('✅ [SIGNUP] تمت العملية بنجاح - يجب أن ينتقل المستخدم للصفحة الرئيسية الآن');
      
    } catch (error: any) {
      console.error('💥 [SIGNUP] خطأ في إنشاء الحساب:');

      // ترجمة أخطاء Firebase للعربية
      let errorMessage = 'فشل في إنشاء الحساب';
      console.error('Firebase error details:', error.code, error.message);
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'هذا البريد الإلكتروني مستخدم بالفعل';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'عنوان البريد الإلكتروني غير صحيح';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'كلمة المرور ضعيفة، يجب أن تكون 6 أحرف على الأقل';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'التسجيل بالبريد الإلكتروني غير مفعل';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'مشكلة في الاتصال بالإنترنت';
      } else if (error.message) {
        errorMessage = `خطأ: ${error.message}`;
      }

      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      console.log('بدء تسجيل الدخول بـ Google...');

      // للويب وExpo Go، نستخدم Firebase Auth
      if (!isNativeEnvironment || !GoogleSignin) {
        try {
          // محاولة استخدام Popup أولاً
          const result = await signInWithPopup(auth, googleProvider);
          const user = result.user;

          const userData: User = {
            id: user.uid,
            name: user.displayName || 'مستخدم Google',
            email: user.email || '',
            photoUrl: user.photoURL || undefined,
            provider: 'google',
          };

          // حفظ في Firestore
          await saveUserToFirestore(userData);

          setUser(userData);
          await AsyncStorage.setItem('user', JSON.stringify(userData));
          console.log('تم تسجيل الدخول بنجاح');
        } catch (popupError: any) {
          // في حالة إغلاق النافذة من قبل المستخدم، لا نعتبرها خطأ
          if (popupError.code === 'auth/popup-closed-by-user') {
            setIsLoading(false); // إعادة تعيين حالة التحميل
            throw popupError; // رفع الخطأ للتعامل معه في auth.tsx بصمت
          }

          // في حالة فشل Popup، نجرب Redirect
          if (popupError.code === 'auth/popup-blocked') {
            console.log('Popup blocked, trying redirect...');
            await signInWithRedirect(auth, googleProvider);

            // التحقق من نتيجة Redirect
            const result = await getRedirectResult(auth);
            if (result?.user) {
              const user = result.user;
              const userData: User = {
                id: user.uid,
                name: user.displayName || 'مستخدم Google',
                email: user.email || '',
                photoUrl: user.photoURL || undefined,
              };

              // حفظ في Firestore
              await saveUserToFirestore(userData);

              setUser(userData);
              await AsyncStorage.setItem('user', JSON.stringify(userData));
              console.log('تم تسجيل الدخول بنجاح عبر Redirect');
            }
          } else {
            throw popupError;
          }
        }
      } else {
        // للتطبيقات المحلية المبنية (standalone apps)
        try {
          console.log('Using native Google Sign-In...');
          await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
          const userInfo = await GoogleSignin.signIn();

          if (userInfo.data?.user) {
            // محاولة تسجيل المستخدم في Firebase أيضاً إذا كان لديه idToken
            try {
              if (userInfo.data.idToken) {
                const { signInWithCredential, GoogleAuthProvider } = await import('firebase/auth');
                const credential = GoogleAuthProvider.credential(userInfo.data.idToken);
                const firebaseResult = await signInWithCredential(auth, credential);
                console.log('تم تسجيل المستخدم في Firebase أيضاً:', firebaseResult.user.email);
              }
            } catch (firebaseError) {
              console.log('فشل في تسجيل المستخدم في Firebase، لكن Google SDK نجح:', firebaseError);
            }

            const userData: User = {
              id: userInfo.data.user.id,
              name: userInfo.data.user.name || 'مستخدم Google',
              email: userInfo.data.user.email,
              photoUrl: userInfo.data.user.photo || undefined,
            };

            // حفظ في Firestore
            await saveUserToFirestore(userData);

            setUser(userData);
            await AsyncStorage.setItem('user', JSON.stringify(userData));
            console.log('تم تسجيل الدخول بنجاح عبر Google SDK');
          }
        } catch (nativeError: any) {
          console.log('Native Google Sign-In failed, falling back to Firebase Auth');

          // إذا فشل Google SDK، نجرب Firebase Auth كبديل
          try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            const userData: User = {
              id: user.uid,
              name: user.displayName || 'مستخدم Google',
              email: user.email || '',
              photoUrl: user.photoURL || undefined,
            };

            // حفظ في Firestore
            await saveUserToFirestore(userData);

            setUser(userData);
            await AsyncStorage.setItem('user', JSON.stringify(userData));
            console.log('تم تسجيل الدخول بنجاح عبر Firebase Auth fallback');
          } catch (fallbackError) {
            throw new Error('Google Sign-In not available on this platform');
          }
        }
      }
    } catch (error: any) {
      // لا نطبع خطأ إلغاء العملية في الكونسول
      if (error?.code !== 'auth/popup-closed-by-user') {
        console.error('خطأ في تسجيل الدخول بـ Google:', error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithTwitter = async () => {
    try {
      setIsLoading(true);
      console.log('بدء تسجيل الدخول بـ X...');

      // للتطبيقات المحلية (الأندرويد)، استخدم Redirect مباشرة
      if (isNativeEnvironment) {
        console.log('استخدام Redirect للتطبيقات المحلية...');
        try {
          await signInWithRedirect(auth, twitterProvider);
          
          // في بيئة native، signInWithRedirect لا يُرجع النتيجة مباشرة
          // بل يعيد توجيه التطبيق، لذا نحتاج للتحقق من getRedirectResult في useEffect منفصل
          // أو يمكننا انتظار قليل ثم التحقق
          setTimeout(async () => {
            const result = await getRedirectResult(auth);
            if (result?.user) {
              const firebaseUser = result.user;

              const userData: User = {
                id: firebaseUser.uid,
                name: firebaseUser.displayName || 'مستخدم X',
                email: firebaseUser.email || '',
                photoUrl: firebaseUser.photoURL || undefined,
                provider: 'twitter',
              };

              await saveUserToFirestore(userData);
              setUser(userData);
              await AsyncStorage.setItem('user', JSON.stringify(userData));
              console.log('تم تسجيل الدخول بنجاح عبر Redirect');
              setIsLoading(false);
            }
          }, 1000);
          
          return; // خروج مبكر للبيئات المحلية
        } catch (redirectError: any) {
          console.error('Redirect failed:', redirectError);
          throw redirectError;
        }
      }

      // للويب، استخدم Popup كما هو
      try {
        // محاولة استخدام Popup للويب
        const result = await signInWithPopup(auth, twitterProvider);
        const user = result.user;

        const userData: User = {
          id: user.uid,
          name: user.displayName || 'مستخدم X',
          email: user.email || '',
          photoUrl: user.photoURL || undefined,
          provider: 'twitter',
        };

        // حفظ في Firestore
        await saveUserToFirestore(userData);

        setUser(userData);
        await AsyncStorage.setItem('user', JSON.stringify(userData));
        console.log('تم تسجيل الدخول بنجاح');
      } catch (popupError: any) {
        // في حالة إغلاق النافذة من قبل المستخدم، لا نعتبرها خطأ
        if (popupError.code === 'auth/popup-closed-by-user') {
          setIsLoading(false);
          throw popupError;
        }

        // في حالة فشل Popup، نجرب Redirect للويب أيضاً
        if (popupError.code === 'auth/popup-blocked') {
          console.log('Popup blocked, trying redirect...');
          await signInWithRedirect(auth, twitterProvider);

          // التحقق من نتيجة Redirect
          const result = await getRedirectResult(auth);
          if (result?.user) {
            const firebaseUser = result.user;

            if (!firebaseUser.uid) {
              throw new Error('فشل في الحصول على معرف المستخدم من X');
            }

            const userData: User = {
              id: firebaseUser.uid,
              name: firebaseUser.displayName || 'مستخدم X',
              email: firebaseUser.email || '', // X قد لا يُرجع البريد الإلكتروني
              photoUrl: firebaseUser.photoURL || undefined,
              provider: 'twitter',
            };

            // حفظ في Firestore
            await saveUserToFirestore(userData);

            setUser(userData);
            await AsyncStorage.setItem('user', JSON.stringify(userData));
            console.log('تم تسجيل الدخول بنجاح عبر Redirect');
          }
        } else {
          throw popupError;
        }
      }
    } catch (error: any) {
      // لا نطبع خطأ إلغاء العملية في الكونسول
      if (error?.code !== 'auth/popup-closed-by-user') {
        console.error('خطأ في تسجيل الدخول بـ X:', error);
      }
      throw error;
    } finally {
      if (!isNativeEnvironment) {
        setIsLoading(false);
      }
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);

      // تسجيل الخروج من Firebase
      try {
        await firebaseSignOut(auth);
      } catch (error) {
        console.log('Firebase signout error:', error);
      }

      // تسجيل الخروج من Google SDK (للتطبيقات المحلية فقط)
      if (isNativeEnvironment && GoogleSignin) {
        try {
          await GoogleSignin.signOut();
          console.log('Google SDK signout successful');
        } catch (error) {
          console.log('Google SDK signout error:', error);
        }
      }

      setUser(null);
      await AsyncStorage.removeItem('user');
    } catch (error) {
      console.error('خطأ في تسجيل الخروج:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithTwitter,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// تصدير افتراضي لحل مشكلة الروتينغ
export default AuthProvider;